import React, { FC } from 'react';
import { Navigate, useParams } from 'react-router-dom';

import { AllRegistrationScreenParams } from '../../utils/navigation';
import RegistrationFormProvider from '../registration/useRegistrationForm/provider';
import { useBuildingData, useBuildingEnergyAnalyses } from '../../hooks/useBuildingDetails/buildingDetailsHooks';
import { createSoknadStegMap } from '../../hooks/useBuildingDetails/utils';
import { LoadingIndicator } from '../../components/loadingIndicator';
import { useGetUserInput } from '../registration/hooks/useGetUserInput';
import useRegistrationForm from '../registration/useRegistrationForm';
import RegistrationForm from '../registration/components/registrationForm';
import { SoknadSteg } from '../../components/registering/utils/registerEnums';

import { getRegId, getRegistrationId, isPublished, isValidSoknad } from './utils';
import { SoknadPublished } from './soknadPublished';
import { UserInputError } from './userInputError';

const SoknadScreen: FC = () => {
  const { soknadId, soknadSteg, buildingId, step } = useParams<AllRegistrationScreenParams>();

  const { building, buildingLoading } = useBuildingData(buildingId);
  const { simpleEnergyAnalyses } = useBuildingEnergyAnalyses(buildingId);
  const { updateSteps } = useRegistrationForm();

  // Populates state with user input for the current soknad step.
  const { isLoading, userInputError } = useGetUserInput(getRegId(soknadSteg, building?.gyldigSoknad), updateSteps);

  if (!step && soknadId) {
    // Navigate to default step if missing
    return <Navigate to="om-boligen" replace />;
  }

  if (buildingLoading || isLoading) {
    return <LoadingIndicator text="loadingContent" />;
  }

  const soknad = building?.gyldigSoknad;

  if (!isValidSoknad(soknadSteg, soknad, soknadId) || !building) {
    return null;
  }

  if (userInputError) {
    return <UserInputError traceId={userInputError.traceId} />;
  }

  const map = createSoknadStegMap(building, simpleEnergyAnalyses);

  if (isPublished(soknadSteg, map, step)) {
    return <SoknadPublished buildingId={building.energiObjektId} />;
  }

  const registrationId = getRegistrationId(soknadSteg || SoknadSteg.Steg1, soknad);

  return (
    <RegistrationFormProvider
      registrationId={registrationId}
      soknad={{
        soknadId: building.gyldigSoknad?.soknadId,
        soknadSteg: soknadSteg || SoknadSteg.Steg1
      }}
    >
      <RegistrationForm />
    </RegistrationFormProvider>
  );
};

export default SoknadScreen;
